import { h, icon, pageNonce } from './dom.js';
import { breakDue, emptyMemory, remember } from './pacing.js';
import { session } from './store.js';

// Ad spots. The dashboard decides which spots are filled and with what (AdSense, MGID, or a custom snippet, which by default runs in a sandboxed frame); this
// file only knows how to draw each kind. A spot nobody filled draws nothing at all.
const loaded = new Map();
function loadScript(src, attrs = {}) {
  if (!loaded.has(src)) {
    loaded.set(
      src,
      new Promise((resolve) => {
        const el = h('script', { src, async: true, nonce: pageNonce(), ...attrs });
        el.addEventListener('load', () => resolve(true));
        el.addEventListener('error', () => resolve(false));
        document.head.append(el);
      }),
    );
  }
  return loaded.get(src);
}

const ADSENSE = /^ca-pub-\d{10,20}$/;
const MGID_HOST = /^https:\/\/[a-z0-9.-]*\.?mgid\.com\//i;

// Ad networks measure the space they are given, and count what they draw. So a spot is drawn only once it is in the page and
// the layout shows it: not while the page is still being put together, and not while it is hidden (the side column on a phone,
// the bottom bar on a computer). A spot that is hidden is drawn if the window changes and shows it.
const waiting = new Set();
const shown = (el) => el.isConnected && el.getClientRects().length > 0;
function recheck() {
  for (const job of waiting) {
    if (!job.el.isConnected) waiting.delete(job);
    else if (shown(job.el)) (waiting.delete(job), job.go());
  }
}
function whenShown(el, go, frames = 600) {
  const tick = () => {
    if (!el.isConnected) return frames-- > 0 ? requestAnimationFrame(tick) : undefined;
    if (shown(el)) return go();
    for (const job of waiting) if (!job.el.isConnected) waiting.delete(job);
    if (!waiting.size) window.addEventListener('resize', recheck, { passive: true });
    waiting.add({ el, go });
  };
  requestAnimationFrame(tick);
}

export function createAds({ api, design, text, consent, mount }) {
  const slots = new Map();
  // A custom ad that runs in a protected frame tells the page how tall it turned out to be, and only the frame it is in is believed.
  const frames = new Set();
  window.addEventListener('message', (e) => {
    if (e.data?.vsAd !== 'size') return;
    for (const frame of frames) {
      if (!frame.isConnected) frames.delete(frame);
      else if (e.source === frame.contentWindow) frame.style.height = `${Math.min(Math.max(Number(e.data.height) || 0, 0), 1200)}px`;
    }
  });
  let offer = null;
  const load = () =>
    api
      .get('v1/config')
      .then((config) => {
        slots.clear();
        for (const ad of config.ads ?? []) slots.set(ad.placement, ad);
        // Whether the site sells ad-free access, and whether this browser has it. (A browser with it is sent no ads.)
        offer = config.subscription ?? null;
      })
      .catch(() => {});
  const ready = load();

  // When the site asks first, nothing is drawn or loaded until the visitor has accepted.
  const everyN = (placement) => (consent.ok() ? (slots.get(placement)?.everyN ?? null) : null);
  const has = (placement) => consent.ok() && slots.has(placement);

  function fill(el, ad) {
    if (ad.network === 'adsense' && ADSENSE.test(ad.config.client ?? '')) {
      el.append(
        h('ins', {
          class: 'adsbygoogle',
          style: 'display:block',
          'data-ad-client': ad.config.client,
          'data-ad-slot': ad.config.slot,
          'data-ad-format': ad.config.format ?? 'auto',
          'data-full-width-responsive': 'true',
        }),
      );
      loadScript(`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ad.config.client}`, { crossorigin: 'anonymous' }).then(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {
          // the network refused this spot; leave it empty
        }
      });
    } else if (ad.network === 'mgid') {
      const loader = design['ads.mgid_loader'];
      el.append(h('div', { id: ad.config.container_id || undefined, 'data-type': '_mgwidget', 'data-widget-id': ad.config.widget_id }));
      const go = () => {
        window._mgq = window._mgq || [];
        window._mgq.push(['_mgc.load']);
      };
      if (typeof loader === 'string' && MGID_HOST.test(loader)) loadScript(loader).then(go);
      else go();
    } else if (ad.network === 'custom' && ad.framed) {
      // The code never reaches this page: it is served to a sandboxed frame without the right to read the page or its storage.
      const frame = h('iframe', { class: 'ad-frame', src: `${mount}_ad/${encodeURIComponent(ad.placement)}`, title: text.t('ad_label'), sandbox: 'allow-scripts allow-popups allow-popups-to-escape-sandbox', scrolling: 'no', referrerpolicy: 'strict-origin-when-cross-origin' });
      frames.add(frame);
      el.append(frame);
    } else if (ad.network === 'custom' && ad.snippet) {
      // A snippet written by staff, which usually carries its own script. Building it as a fragment makes the browser run it.
      const fragment = document.createRange().createContextualFragment(ad.snippet);
      const nonce = pageNonce();
      if (nonce) for (const script of fragment.querySelectorAll('script')) script.nonce = nonce;
      el.append(fragment);
    }
  }

  /** An ad spot, ready to put in the page. Returns null when the spot is not filled, so callers can skip it. */
  function slot(placement, { card = false } = {}) {
    const ad = consent.ok() ? slots.get(placement) : null;
    if (!ad) return null;
    const el = h('div', { class: `ad ${card ? 'card-ad' : ''}`, dataset: { placement } }, h('span', { class: 'label' }, text.t('ad_label')));
    // Drawn once the element is in the page and showing, because ad scripts measure the space they will fill.
    whenShown(el, () => fill(el, ad));
    return el;
  }

  const number = (v, fallback) => (Number.isFinite(Number(v)) && v !== '' && v !== null && v !== undefined ? Number(v) : fallback);

  /**
   * The break before an episode, when the site's pacing says one is due (see lib/pacing.js), else null. Asking for it counts as
   * showing it, so ask only when the episode is about to play.
   */
  function interstitial(placement, { drama, episode }) {
    const key = `${drama}:${episode}`;
    const memory = session.get('ads-pace', emptyMemory());
    const now = Date.now();
    const due = breakDue({
      episode,
      free: number(design['ads.free_episodes'], 2),
      every: everyN(placement),
      memory,
      now,
      gapMs: number(design['ads.gap_minutes'], 3) * 60_000,
      cap: number(design['ads.max_per_visit'], 0),
      key,
    });
    const el = due ? slot(placement) : null;
    if (el) session.set('ads-pace', remember(memory, key, now));
    return el;
  }

  /** The bar fixed to the bottom of small screens, which the visitor can close. Once closed it stays closed for the rest of the visit. */
  function sticky() {
    const ad = consent.ok() && !session.get('sticky-closed', false) ? slots.get('sticky_bottom') : null;
    if (!ad) return null;
    const bar = h('div', { class: 'ad-sticky show' });
    bar.append(h('button', { type: 'button', 'aria-label': text.t('close'), on: { click: () => (session.set('sticky-closed', true), bar.remove()) } }, icon('close')));
    const inner = h('div', { class: 'ad' }, h('span', { class: 'label' }, text.t('ad_label')));
    bar.append(inner);
    whenShown(inner, () => fill(inner, ad));
    // The page makes room for the bar (and lifts its own bottom notices above it), so it never hides the end of a page.
    if (typeof ResizeObserver !== 'undefined') {
      const root = document.documentElement.style;
      new ResizeObserver(() => root.setProperty('--ad-bar', `${bar.isConnected ? bar.offsetHeight : 0}px`)).observe(bar);
    }
    return bar;
  }

  return { ready, slot, sticky, interstitial, everyN, has, offer: () => offer, refresh: load };
}
